/**
 * This is a base component for background video (Uses the base component base/video.js for the video).
 * @author Hampus Lindholm & Anton Pedersen
 * @version 1.0
 *
 * @param {node} children - JSX children
 * @param {string} className - Used by styled components (Add option to style this component)
 * @param {string|object|string[]|object[]} src - One or several video sources as strings or objects
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { imgixSrc } from "core-web/libs/image";
import BaseVideo from "core-web/components/base/video/Video";
import { useVideoAutoPlayback } from "core-web/hooks/useVideoAutoPlayback";
import styled from "theme/libs/styled";

const BackgroundVideoStyle = styled("div")`
    position: relative;
    z-index: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

const Video = styled(BaseVideo)`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    object-fit: cover;
    z-index: -1;
`;

const BackgroundVideo = ({ children, className, loading, src, poster, query, ...rest }) => {
    const imgixPoster = poster ? imgixSrc(poster, query) : null;

    const [containerRef, videoRef /*, isPlaying*/] = useVideoAutoPlayback({
        root: null,
        rootMargin: "0px",
        threshold: 0.15,
    });

    return (
        <BackgroundVideoStyle className={className} ref={containerRef}>
            <Video
                {...rest}
                loop
                muted
                autoPlay
                playsInline
                videoRef={videoRef}
                controls={false}
                src={src}
                poster={imgixPoster}
                loading="lazy"
            />
            {children}
        </BackgroundVideoStyle>
    );
};

BackgroundVideo.propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
    className: PropTypes.string,
    loading: PropTypes.string,
    poster: PropTypes.string,
    query: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
    src: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.exact({
            src: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
        }),
        PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.exact({
                    src: PropTypes.string.isRequired,
                    type: PropTypes.string.isRequired,
                }),
            ]),
        ),
    ]).isRequired,
};

BackgroundVideo.defaultProps = {
    children: false,
    className: "",
    loading: "lazy",
    poster: "",
    query: { auto: "format", q: 70, w: 700 },
};

export default BackgroundVideo;
